interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** 表示件数情報（オプション） */
  totalItems?: number;
}

export default function Pagination({ currentPage, totalPages, onPageChange, totalItems }: PaginationProps) {
  if (totalPages <= 1) return null;

  const pages = [];
  // スマホでは表示ページ数を減らす
  const showPages = 3;
  let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
  const endPage = Math.min(totalPages, startPage + showPages - 1);
  
  if (endPage - startPage + 1 < showPages) {
    startPage = Math.max(1, endPage - showPages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
      {/* 件数表示（オプション） */}
      {totalItems !== undefined && (
        <div className="text-xs sm:text-sm text-gray-500 order-2 sm:order-1">
          全 {totalItems.toLocaleString()} 件中 {currentPage} / {totalPages} ページ
        </div>
      )}
      
      {/* ページネーションボタン */}
      <div className="flex items-center gap-0.5 sm:gap-1 order-1 sm:order-2">
        {/* 前へボタン */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="p-2 sm:px-3 sm:py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="前のページ"
        >
          {/* スマホ: アイコン、PC: テキスト */}
          <svg className="w-5 h-5 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="hidden sm:inline">前へ</span>
        </button>
        
        {/* 最初のページ */}
        {startPage > 1 && (
          <>
            <button
              onClick={() => onPageChange(1)}
              className="w-9 h-9 sm:w-auto sm:h-auto sm:px-3 sm:py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center justify-center"
            >
              1
            </button>
            {startPage > 2 && <span className="px-1 sm:px-2 text-gray-400 text-sm">...</span>}
          </>
        )}

        {/* ページ番号 */}
        {pages.map((page) => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`w-9 h-9 sm:w-auto sm:h-auto sm:px-3 sm:py-1.5 text-sm rounded flex items-center justify-center ${
              page === currentPage
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            {page}
          </button>
        ))}

        {/* 最後のページ */}
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="px-1 sm:px-2 text-gray-400 text-sm">...</span>}
            <button
              onClick={() => onPageChange(totalPages)}
              className="w-9 h-9 sm:w-auto sm:h-auto sm:px-3 sm:py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center justify-center"
            >
              {totalPages}
            </button>
          </>
        )}

        {/* 次へボタン */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="p-2 sm:px-3 sm:py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="次のページ"
        >
          {/* スマホ: アイコン、PC: テキスト */}
          <svg className="w-5 h-5 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="hidden sm:inline">次へ</span>
        </button>
      </div>
    </div>
  );
}
